import {
  Box,
  useTheme,
  Typography,
  Breadcrumbs,
  IconButton,
  Link,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import { Toolbar } from './project/Toolbar';
import { useTranslation } from 'react-i18next';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import useFetch from '../../api/useFetch';
import { ProjectPhaseStack } from './project/phase/ProjectPhaseStack';
import Header from '../special/Header';
import { useEffect, useRef, useState } from 'react';
import ExoDialog from './ExoDialog';
import NewEmptyProject from '../new/NewEmptyProject';
import { NavLink, useNavigate } from 'react-router-dom';
import { hasPermissions } from '../../auth/hasPermissions';
import BarChartIcon from '@mui/icons-material/BarChart';
import { ProjectUserStack } from './project/ProjectUserStack';
import { tokens } from '../../global/theme/tokens';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExoAvatar from './ExoAvatar';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import EmailIcon from '@mui/icons-material/Email';
import { ProjectEventList } from './project/events/ProjectEventList';
import { ExoGant } from './ExoGant';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import { CHROverview } from './project/CHROverview';
import SaveIcon from '@mui/icons-material/Save';
import { NewProjectPreset } from '../../scenes/projects/NewProjectPreset';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import LayersIcon from '@mui/icons-material/Layers';
import Cookies from 'universal-cookie';
import { ShowProjectsInteractions } from './project/ShowProjectsInteractions';
import ProjectParticipantCard from './project/ProjectParticipantCard';
import RenderHTML from '../preview/RenderHTML';

export const ExoProject = ({ projectId = 18, allowEditMode }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const cookies = new Cookies();
  const cookieProjectView = cookies.get('project-view') || 'tree';

  const [openSaveAsPreset, setOpenSaveAsPreset] = useState(false);
  // project
  const { apiData, setApiData } = useFetch(`projects/${projectId}`);
  const [open, setOpen] = useState(false);

  const { address, events, nr, name, client, prefix, clientType } = apiData
    ? apiData.data
    : {};

  function handleUpdate(data) {
    setApiData({ ...apiData, data: data });
  }

  // events change
  function handleEventsChange(events) {
    setApiData({ ...apiData, data: { ...apiData.data, events } });
  }

  // project management
  const [inEditMode, setInEditMode] = useState(false); // editMode toggle from Project is always hidden

  const isEditAllowed = apiData?.data?.editable;
  const toollist = [
    {
      Icon: SettingsApplicationsIcon,
      label: t('Settings'),
      onClick: () => setOpen(true),
    },
    {
      Icon: BarChartIcon,
      label: t('Analytics'),
      onClick: () => navigate(`/projects/${projectId}`),
    },
    {
      Icon: SaveIcon,
      label: t('Save as preset'),
      onClick: () => setOpenSaveAsPreset(true),
      hidden: !hasPermissions('project', 'write'),
    },
    {
      Icon: BookmarksIcon,
      label: t('Presets'),
      onClick: () => navigate('/presets/project'),
      hidden: !hasPermissions('sales-object', 'write'),
    },
  ];

  const supervisor = apiData && apiData.data.supervisor;

  // view
  const [activeView, setActiveView] = useState(cookieProjectView);
  function changeView(e, value) {
    if (activeView !== value && value) {
      setActiveView(value);
      cookies.set('project-view', value);
    }
  }

  return (
    <Box className="h-full w-full flex flex-col mx-auto">
      <Box
        className="flex flex-col gap-2 md:sticky top-0 z-10"
        sx={{
          bgcolor: colors.glass,
          backdropFilter: 'blur(10px)',
          padding: '10px 15px',
          borderRadius: '8px',
        }}
      >
        <Breadcrumbs aria-label="breadcrumb">
          <NavLink
            to={'/projects'}
            underline="hover"
            className="flex items-center"
            color="inherit"
          >
            <AccountTreeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('Projects')}
          </NavLink>
          <Typography
            sx={{ color: 'text.primary' }}
            className="flex items-center"
          >
            {apiData && apiData.data.image && (
              <img
                className=" h-5 w-5 object-cover mr-2 rounded-full"
                src={apiData.data.image.temporaryUrl}
              />
            )}
            {`${nr} ${name}`}
          </Typography>
        </Breadcrumbs>
        <Box className="flex gap-0 md:gap-8 md:items-center justify-between md:justify-start pr-2 md:flex-row flex-col pb-4 md:pb-0 ">
          <Header title={`${nr} ${name}`} />

          <ToggleButtonGroup
            size="small"
            value={activeView}
            exclusive
            onChange={changeView}
            aria-label="Project Status"
            className="backdrop-blur-lg"
          >
            <Tooltip title={t('Overview')}>
              <ToggleButton value="tree">
                <AccountTreeIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t('Timeline')}>
              <ToggleButton value="gant">
                <ViewTimelineIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t('Interactions')}>
              <ToggleButton value="interactions">
                <LayersIcon />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>
        {apiData?.data?.description && (
          <RenderHTML HTML={apiData?.data?.description || ''} />
        )}
        {apiData && apiData.data.editable && (
          <Toolbar
            tools={toollist}
            setInEditMode={setInEditMode}
            inEditMode={inEditMode}
            hidden={true}
            disableElevation
          />
        )}
      </Box>

      {apiData &&
        apiData.data.events &&
        Boolean(apiData.data.events.length) &&
        activeView !== 'interactions' && (
          <Box className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 w-full gap-2">
            <ProjectEventList events={events} />
            <Box className="mt-4 backdrop-blur-lg">
              <CHROverview className="px-2 py-2" projectId={apiData.data.id} />
            </Box>
          </Box>
        )}

      {activeView === 'gant' && apiData && (
        <Box className="pt-2 md:h-full min-h-full md:min-h-fit">
          <ExoGant
            projectId={apiData?.data?.id}
            inEditMode={isEditAllowed}
            editable={apiData?.data?.editable}
          />
        </Box>
      )}

      {activeView === 'tree' && apiData && (
        <ProjectPhaseStack project={apiData.data} editmode={isEditAllowed} />
      )}

      {activeView === 'interactions' && apiData && (
        <ShowProjectsInteractions
          project={apiData.data}
          editmode={isEditAllowed}
          events={events}
          onEventsChange={handleEventsChange}
          onProjectChange={setApiData}
        />
      )}

      {apiData && (
        <Box
          className=" p-4 rounded-lg mt-4 backdrop-blur-lg flex justify-between items-start flex-wrap"
          sx={{ bgcolor: colors.glass }}
        >
          <Box>
            <Box>
              <Typography variant="h6" className="pb-2">
                {t('Supervisor')}
              </Typography>
              <Box className="flex gap-4">
                <ExoAvatar
                  picture={
                    supervisor.picture && supervisor.picture.temporaryUrl
                  }
                  name={`${supervisor.firstName} ${supervisor.lastName}`}
                />
                <Box className="flex flex-col">
                  <Typography variant="h4">{`${supervisor.salutation} ${supervisor.firstName} ${supervisor.lastName}`}</Typography>

                  <Box className="flex items-center gap-1">
                    <Link href={`tel:${supervisor.phone}`}>
                      <IconButton size="small">
                        <PhoneIphoneIcon />
                      </IconButton>
                    </Link>
                    <Typography>{supervisor.phone}</Typography>
                  </Box>

                  <Box className="flex items-center gap-1">
                    <Link href={`mailto:${supervisor.email}`}>
                      <IconButton size="small">
                        <EmailIcon />
                      </IconButton>
                    </Link>
                    <Typography>{supervisor.email}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box>
              <Typography variant="h6" className="pb-2">
                {t('Team')}
              </Typography>
              <Box className="flex">
                <ProjectUserStack
                  noLabel
                  displayCount={8}
                  editmode={apiData.data.editable}
                  users={apiData.data.teamMembers}
                  api={'users'}
                  changeApi={
                    'project/' + apiData.data.id + '/projectassignment'
                  }
                  onChange={(res, newMembers) =>
                    setApiData({
                      ...apiData,
                      data: { ...apiData.data, teamMembers: newMembers },
                    })
                  }
                  //component={CHRSelector}
                  //project={apiData.data}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <ExoDialog open={open}>
        <NewEmptyProject
          onClose={() => setOpen(false)}
          onCreate={handleUpdate}
          startDataCollection={apiData && apiData.data}
        />
      </ExoDialog>

      <ExoDialog
        open={openSaveAsPreset}
        limitWidth
        onClose={() => setOpenSaveAsPreset(false)}
      >
        <NewProjectPreset
          projectId={apiData?.data.id}
          onClose={() => setOpenSaveAsPreset(false)}
        />
      </ExoDialog>
    </Box>
  );
};
